/**
 * Pure procedural synthesis of the authentic Minecraft Java Edition 1.8.8
 * 3D stone-carved title logo for "VOXELCRAFT".
 *
 * Implements the official Minecraft title screen dimensions (310x44 pixels)
 * and 11-color palette with cracked stone face, chisel bevel highlights,
 * and 3D downward block extrusion. Zero external image files (Rule 3).
 */

export const TITLE_LOGO_WIDTH = 310;
export const TITLE_LOGO_HEIGHT = 44;

/**
 * Minecraft title palette:
 * 0: transparent
 * 1: black border (#000000)
 * 2: dark 3D bottom extrusion (#1e1c1f)
 * 3: dark 3D bottom extrusion 2 (#231e1e)
 * 4: dark cracks / inner shadow (#454243)
 * 5: mid shadow (#535053)
 * 6: base stone face (#837c7c)
 * 7: light stone face (#a59c99)
 * 8: light stone face 2 (#a89f9c)
 * 9: bright stone face (#afa4a0)
 * 10: chisel bevel highlight (#c8c3bf)
 */
const TITLE_PALETTE_RGBA: readonly [number, number, number, number][] = [
  [0, 0, 0, 0],          // 0: transparent
  [0, 0, 0, 255],        // 1: black outline (#000000)
  [30, 28, 31, 255],     // 2: dark 3D bottom extrusion (#1e1c1f)
  [35, 30, 30, 255],     // 3: dark 3D bottom extrusion 2 (#231e1e)
  [69, 66, 67, 255],     // 4: dark cracks / inner shadow (#454243)
  [83, 80, 83, 255],     // 5: mid shadow (#535053)
  [131, 124, 124, 255],  // 6: base stone face (#837c7c)
  [165, 156, 153, 255],  // 7: light stone face (#a59c99)
  [168, 159, 156, 255],  // 8: light stone face 2 (#a89f9c)
  [175, 164, 160, 255],  // 9: bright stone face (#afa4a0)
  [200, 195, 191, 255],  // 10: chisel bevel highlight (#c8c3bf)
];

const LOGO_DATA_B64 = [
  "AAAAAAAAAAAAAAABEREREREQAAEREREREREREREREREREREREREREREREREREAABEREREREREREREREREREREREREQERERERAAAA",
  "AAAAAAABERERERERERERERERARERERERERERERERABEREREREREREREREAEREREREREREREREQERERERERERERERERAAAAAAAAAA",
  "AAAAAAAAAAAAAAAAAAAAAAGqqqqqoQAAABqqqqqqEaqqqqqqqqqqqqqqqqoRqqqqqqEAAAAaqqqqqhZ3d3d3d3d3d3d3d3dxCqqq",
  "qqEAAAAAAAAAAAF3d3d3d3d3d3d3d3cBd3d3d3d3d5d3d3cAd3d3d3d3d3d3d3dxF3d3d3d3d2d3d3d3AXd3d3d3l3d3d3d3cAAA",
  "AAAAAAAAAAAAAAAAAAAAAAAAAAAAAXd3d3ZxAAAAF3d3d2cRZ3d3d3d3d3d3d3d3dxF3d3d2cQAAABd3d3Z3F3d3d3d5d3d3l3d3",
  "d3EHd3d3cQAAAAAAAAAAAXd3d3d3d3d3d3d3dwF3l3d3d3d3d3d3dxB3d3d5d3d3l3d3d3EXd3d3d3d3V3d3l3cRd3d3d3d3d3Z3",
  "d3dxAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABd5d3d3EAAAAXeXd3dxF3eXd3d3d3eXd3d3eXEXeXd3dxAAAAF5d3d3cXd3d3d3d3",
  "d3d3d3eXcQd3d3dxAAAAAAAAAAABd5d3d3d3d3d3d3dxAXd3d3eXd3d3eXd3EHd3d3d3d3d3d3eXdReXd3d3d3d1d3d3dxFnd5d3",
  "d3eXdXd3d3EAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGXl3d3kQAAABl5d3d5EXl5d3d3d3d5d3d3d5cRl5d3d5cQAAGXl3d3lxd3",
  "d3d3d5d3d3d3d3dxB3d3d3EAAAAAAAAAAAF5l3d3d3d3d3d3d3EBd3d3d3d3d3l5d3cQZ3d3d5d3d3d3d3d2F5d3d3d3d3dVd3l3",
  "UVd3d3d3d5dyd3d3cQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABd5d5dxAAAAF3l3l3EBd3l3l3eXd3l3d3l3lxF3l3l3dxAAAXeX",
  "eXd3F3d3d3d3d3d3d3d3d3EHd3d3cQAAAAAAAAAAAXeXl3d3d3d3d3d3cQF3d3d3d3d3d3l5dxFXd3d3d3d3d3d3d3cXl3l3d3d3",
  "d3J3d3dhF3d3dnd3l5cnd3dxAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAF3d3d3EAAAAXd3d3cQF3d3d3d3eXd3d5d3d3EBd3d3d3",
  "EAABd3d3d3EHeZd5d3d6qqqqqqqnUQd5l3lxAAAAAAAAAAABd3d3l3d6qqqqqqqhAXd3d3d3d3d3d3d5ERmXmqp3d3eqp3d3dxZ3",
  "d5d3qqqqoaqqp3EXqqpld3d3dxeqqqEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAXl3d3cQAAABeXd3dxAXeXd3d3d3eXd3d3d3kQ",
  "GXd3d3kQAAF5d3d3cQd3d3eXdxERERERERERB3d3d5EAAAAAAAAAAAF3d3d3l1EREREREREBd3dnd3ERV3eXd3cRF3d1ERF3d1ER",
  "J3d3FXd3eXdxERERERERERERERV3eXd3EREREQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABd3mXeXEAABd3mXeXEBl3eZcRERERER",
  "F3eZdxABeZd5dxAAAXd5l3kQB3d3d3eXEREREREREREHd3d3cQAAAAAAAAAAAZd5d3d3ERERERERERF3d1d3cREZl3eXd1EXd3UR",
  "EXd3EREXeXcReZd3d3EREREREREREREREXmXeXd1EREQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAXd3d3cQAAF3d3d3EAF3d3dx",
  "REREREQXd3d3EAF3d3d3EAABd3d3dxAGZVVmd3YREREREREREQZlVWZxAAAAAAAAAAABd3d3d3cREREREREREXqnV3d1ERd3d3d3",
  "YRmXdxERqqcRERd3dxF3d3d3lRERERERERERERERV3d3d3cRERAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABl5eqpxAAAZeXqqcQ",
  "AXl5eqFQAAAAABeqp5cQABeqp5eRABeXl6qhAAd3d3d3dRERERERERERB3d3d3EAAAAAAAAAAAF5mqp3dxERERERERERJWZXd3cR",
  "F3l5d3dxF3d3d3cREld3d3eXEZd3d3d3EREREREREREREREXd5d3dxERAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAF3ZmVXEAAB",
  "d2ZlVxABd3ZmUVAAAAAAElVmZxAAFmVXd2EAF3dmZVEAB3d3d3dxEREREREREREHd3d3cQAAAAAAAAAAAXd2VSJ3ERERERERERFX",
  "d1d3dxEXd3d3d3EXd3d3qhERWqd3d5cRZ3d3d3cRERERERERERERERd3d3d3UREAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAXd3",
  "eXcQAAF3d3l3EAF3d3eRUAAAAAAXeXd3EAABeXd3cQAXd3d5EAAHd3d3d3EREREREREREQd3d3dxAAAAAAAAAAABd3eXdyIRERER",
  "EREREXd5V3d3ERd3d3l3cRd3d3chERERF3d3dxFXeXd3d1ERERERERERERERF3d3l3dxEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA",
  "AAAAF3d3dxAAAXd3d3EAAXd3d3FQAAAAABd3d3cQAAF3d3dxABd3d3cQAAd3d3d3eZd3dxV3d3cRB3d3d3EAAAAAAAAAAAF3d3d3",
  "dRERERERERERd3dnd3d3d3d3d1ERFnd3eREREREVd3d3ERd3d5l3d3d3dpl3dREREREVd3d3d3EAAAAAAAAAAAAAAAAAAAAAAAAA",
  "AAAAAAAAAAAXd3d3EAABd3d3cQABd3d3cVAAAAAAF3d3dxAAABd3d3d3d3d3cQAAB3d3d3d3d3d3J3d3mREHd3d3cQAAAAAAAAAA",
  "AXd3d3d5EREREREREBF3l3d3eXd3d3d3EREVd3d3ERERERF3mXdRF3d3d3d3d3d1d3d2ERERERF3d3d3cQAAAAAAAAAAAAAAAAAA",
  "AAAAAAAAAAAAAAAAABd3d3cQAAF3d3dxAAF3d3dxUAAAAAAXd3d3EAAAF3d3d3d3d3dxAAAHd3d3l3d5d3cpd3l5EQd3d3eRAAAA",
  "AAAAAAABd3d3d3cREREREREQEXd5d3l3d3d3d3URERF3d3dREiIhEXl5d2EXd3d3d3d3eXV3l3cREREREXd3d3d1EAAAAAAAAAAA",
  "AAAAAAAAAAAAAAAAAAAAAAAAF3d3dxAAAXd3d3EAAZd3d3FQAAAAABd3d3cQAAABeXd3d3l3dxAAAAd3d3d3d3d3d1d3d3cRB3d3",
  "d3EAAAAAAAAAAAGXd3d3dxERERERERARd3d3d3d3d5d3d3dxEXd3d3EXd3cRd3d3kRd3d3d3d3d3cnd3dxERERERV3d3d3cQAAAA",
  "AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABZnd3EAABZnd3EAABJmd3cVAAAAAAF3d3dhAAAAASZnd3cmZxAAAAB3d3d3d5d3d3Z3d3",
  "dxEHd3d3cQAAAAAAAAAAASd3d3d2EREREREREBF3l3d3d3d3d3d3d3ERd3d5cRd3dxF3d3dxFnd3d3d3d3dyl3d5UREREREXd3d3",
  "dxEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAF3l3cQAAF3l3cQAAFneXdxUAAAAAAXd3eXEAAAAXZ3l3d2d5cQAAAHd3d3d3d3",
  "d3dnd3eXEQd3d3dxAAAAAAAAAAABd5d3d3UREREREREQEXd3d3eXd3d3eXd3dRF3d3d3d3d3d3eXd3EVd3d3d3d3dyJ3d3dhERER",
  "EReXd3d3UQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAZeXdxAAAZeXdxAAAXl5d3FQAAAAABd3d5cQAAAXd5eXd3eXl3EAAAd3",
  "d3d3d6qqqlqqqnURB3d3d3EAAAAAAAAAAAF5l3d3cQAAAAAAAAARV3d3d3eqp3l5d3d2EXd3d5d3d3d3d3d3cRF3d3d3d6qlIaqq",
  "p3ERERERF5d3d3dxAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABd5d5EAABd5d5EAABd3l3kVAAAAAAF3l3lxAAABl3d5d5d3eX",
  "cQAAB3d3d3dxEREREREREREHd3d3cQAAAAAAAAAAAXeXl3dxAAAAAAAAAAFXd3d3dxEVd3l5d3cRd3d3d3d3d3d3d3dxEXl3d3d3",
  "EREREREREREREREXl5d3d3EAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAXd3cQAAF3d3EAAAF3d3dxUAAAAAAZd3d3EAABd3d3",
  "cQAXd3d3EAAHeZd5d3EREREREREREQd5l3lxAAAAAAAAAAABd3d3l3EAAAAAAAAAARd3d3d3ERF3qqd5dxGXl3d3d3d3d3d3d3ER",
  "d5d3d5kREREREREBERERERd3d5d3dRAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABeXd3EAF5d3cQAAAXeXd3FQAAAAABd3d3kQ",
  "AAF3d3lxABd5d3cQAAd3d3eXYRERERERERERB3d3d5EAAAAAAAAAAAF3d3d3kQAAAAAAAAABF3d3d3cREXd3d3d3EWd3d3d3qqqn",
  "d3d3cRFneXd3dxEREREREQARERERGaqqd5d3EAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAF3eZcQAXd5lxAAABl3eZcVAAAAAA",
  "F3eZdxAAGZd5d3EAGXd5l3EACqp3d3dREREREREREREKqnd3cQAAAAAAAAAAAZd5d3dxAAAAAAAAAAEXd3d3eRERl3eXd3cRV3eX",
  "d3dREXd3eXeREVd3d3d3UREREREQABERERESIlVVZ3cQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAXd3dxABd3d3EAAAF3d3dx",
  "UAAAAAAXd3d3EAAXd3d3cQAXd3d3cQAGZmd3dxEREREREREREQZmZ3dxAAAAAAAAAAABd3d3d3EAAAAAAAAAARd3qqp3URF3d3d3",
  "dxEXd3eXeVERd6qqd3ERF3d3l5lxERERERAAERERESEXd3d3dxAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGXl3EAGXl3EAAA",
  "AXl5d3ERERERERd3d5cQAZd3d5cQAAGXl3d3EAd3Z3d3ERERERERERERB3dnd3ERERERERERERF5l3d3dREREREREREBF3d2VSIh",
  "EXl5d3d3ERd3d3d3cRFWd2VSVREXd3d3d3ERERERAAARERERIZd3d3d3UAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAXd3cQAX",
  "d3cQAAABd3d3d3d3d3d3d3d3dxABd3d3dxAAAXd3d3cQB3dnd3d3d5d3d3d3ZxEHd2d3d3d3l3d3d3dnEXd3d3d3d3d3d3d3dwEX",
  "d3d3d3ERd3d3d3cRF3d3d5dxEVd3d5ciERd3d3d3cREREREAABEREREhd3d3d3dwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAB",
  "d3d5EXd3eRAAAAF3d3eXd5d3d3d3eXd3EBd3eXd3EAABd3d5d3EHd1d3d3d3d3d2ZmZ3EQd3V3d3d3d3d3ZmZncRd3eXd3d3d3d3",
  "d3d3ARdpd3d3cRF3d3l3dxEXd3d3d3ERF3d3d3IRGXd3d3dxEREREQAAEREREhFXl3d3d3EAAAAAAAAAAAAAAAAAAAAAAAAAAAAA",
  "AAAAAAF3d3d3d3d3EAAAAXd3d3d3d3d3d3d3d3cQF3d3d3cQAAF3d3d3cQd3V3d3eZd3d2d3d3cRB3dXd3d5l3d3Z3d3dxF3d3d3",
  "d3d5l3d3d3cBF1d3d3dxEVd3d3eZERd3eZd3cREXd3d3dxEWd5l3d3UREREQAAARERESERd3d3d3cQAAAAAAAAAAAAAAAAAAAAAA",
  "AAAAAAAAAAAAABd3d3eXd3EAAAABl3d3d3d3d3d3d3d3dxF3d3d5dxAAAXd3d3l3F3cnd3d3d3d3Z3d3dxEHdyd3d3d3d3dnd3d3",
  "EZd3d3d3d3d3cnd3dwEXV3d3d3ERV3d3d3cRF3d3d3dxERd3d3eXERV3d3d3dxERERAAABERESERF3d3d3dxAAAAAAAAAAAAAAAA",
  "AAAAAAAAAAAAAAAAAAAAF3d3d3d3cQAAAAF3d3d3d3d3d3d3d3d3EXd3d3d3EAABd3d3d3cXdyV3d3l3d3dXd3d3EQd3JXd3eXd3",
  "d1d3d3cRd3d3d3d3d3dyd3d3AReXd3d3cREXd3d3dxEXd3l3d3URF3d3d3cREXd3d3d3ERERAAAAAREREREXd3d3d3IQAAAAAAAA",
  "AAAAAAAAAAAAAAAAAAAAAAAAAAAXeXYid3lxAAAAAXd5diJ2IhV5dlEiZ5cRd5diJ3EAAAAXl2Indxd3EXd3d3d3dxd3d5cRB3cR",
  "d3d3d3d3F3d3lxF3liIVd3d3d3F3d3cBEnd3d5dxERd5dnd3ERV3d3d3dREXd5d3dxERd3d3d3cQAAAAAAABERERERV3d3d3dxAA",
  "AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABqqYlJqqmEAAAABaqpiUmJSEVpiESUmqhGqpiUmoQAAABqmJSaqGlURVXqqqqqnF6qq",
  "phEKVRFVeqqqqqcXqqqmEWqiUhFWqqqqoRqqpgESqqqqqmERFqqhqqoRFaqqGqqmERWqqqqmERGqqqqqphAAAAAAAAEREREREqqq",
  "qqqmEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAaqqqqqqEAAAAAGqqqqqqqqqqqqqqqqqEaqqqqqhAAAAGqqqqqoSIlEiIiIi",
  "IiISIiIiEQIiUSIiIiIiIhIiIiIRIiJVESIiIiIhEiIiARIiIiIiIREiIiEiIhESIiIVIiIREiIiIiIRESIiIiIiEAAAAAAAARER",
  "ERESIiIiIiIQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABIiIlIiIQAAAAASIiIlISIiIiIiEiIiIRIiIlIiEAAAASIiUiIhIi",
  "UhIiIiIiIiIiIiIRAiJSEiIiIiIiIiIiIhEiIlISIiIiIiEiIiIBIiIiIiIhESIiIVIiESIiIiUiIREiIiIiIRERIiIiIiEAAAAA",
  "AAAAERIRESIiIiIiIQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEiIlISIhAAAAABIiIlISIiIiIiElIiIhEiIlISIQAAABIi",
  "UhIiEiIlEiIiIiIiIiIiIhECIiUSIiIiIiIiIiIiESIlISIiIiIiElIiIQEiIiIiIhARIiIiUiIRIiIiIiIhESIiIiIhIRIiIiIi",
  "IQAAAAAAAAARESESIiIiIiIgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAASIiISIiEAAAAAEiIiISIiIiIiIVUiIiESIiISIh",
  "AAAAEiIhIiISIiUSIiIiIiIiIiIiEQIiJRIiIiIiIiIiIiIRIiISIiIiIiIVUiIhASIiIiIiEBEiIiIiIREiIiIiIiESIiIiIiEi",
  "EiIiIiIQAAAAAAAAABERIRIiIiIiIhAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABIiIhIiIQAAAAASIiIhIiIiIiIhUiIiIR",
  "IiIhIiEAAAASIiEiIhIiIhIiIiIiIiIiIhERAiIiEiIiIiIiIiIiEREiIhIiIiIiIhUiIiEBIiIiIiIQESIiIiIhESIiIhERERIi",
  "IiIiIRIRIiIiIQAAAAAAAAAAERESESIiIiIhAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAASIiIiIQAAAAABIiIiIiIiIiIi",
  "EiIiIhEiIiIiIQAAABIiIiIiEiIiIiIiIiIiIiERERECIiIiIiIiIiIiIRERESIiIiIiIiIiEiIiIQEiIiIiIQASIiIiIRESIiJV",
  "VSEREiIiIiIRERERIiIQAAAAAAAAAAABERIRESIiIhAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABEiIiIRAAAAAAERIiIi",
  "IiIiIiISIiIiERIiIiERAAAAEiIiIRISIiIiIiIiIiIiIiIiEQIiIiIiIiIiIiIiIiIRESIiIiIiIiISIiIhAREiIiIhABIiIiIi",
  "EBIiIiIiEBEiIiIiIhESIlVVIhAAAAAAAAAAAAERESIiIiIhAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAERESIhEAAAAA",
  "AREREiIiIiIiISIiIiIREREiIREAAAARESIhERIiIiIiIiIiIiIiIiIRAiIiIiIiIiIiIiIiIhAREiIiIiIiISIiIiEBIREREREA",
  "EiIiIiIQEiIiIiIQE0RCIiIiARIiIiIhAAAAAAAAAAAAARESIiIiIhAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAASIiIi",
  "EQAAAAABEiIiIiIiIiIhIiIiIhEiIiIhIQAAABIiIiEiEiIiIiIiIiIiIiIiIhECIiIiIiIiIiIiIiIiEBIiIiIiIiIhIiIiIQEi",
  "VSIiIQASIiIiIhASIiIiIhASIiIiIiEBIiIiIhAAAAAAAAAAAAABERIiIiIhAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA",
  "AAEiIiIQAAAAAAESIiIiJCIiIiEiIiIiESIiIiEhAAAAEiIiISISIiIiIiIiIiIiIiIiEAIiIiIiIiIiIiIiIiIQEiIiJCIiIiEi",
  "IiIhASIiIiIQABIiIiIiABIiIiIhABIiIiIiIQEiIiIhEAAAAAAAAAAAAAARIiIiIRAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA",
  "AAAAAAAAARERERAAAAAAAREREREREREREREREREREREREREAAAAREREREREREREREREREREREREQARERERERERERERERERARERER",
  "MREREREREREBERERERAAEREREREAERERERAAEREREREQAREREREAAAAAAAAAAAAAABEREREQAAAAAAAAAAAAAAAAAAAAAA==",
].join("");

/**
 * Draws the authentic 310x44 Minecraft 3D stone logo onto the provided canvas.
 */
export function renderTitleLogo(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  canvas.width = TITLE_LOGO_WIDTH;
  canvas.height = TITLE_LOGO_HEIGHT;

  const binary = atob(LOGO_DATA_B64);
  const imgData = ctx.createImageData(TITLE_LOGO_WIDTH, TITLE_LOGO_HEIGHT);
  const data = imgData.data;

  for (let i = 0; i < binary.length; i++) {
    const byte = binary.charCodeAt(i);
    const p1 = (byte >> 4) & 0x0f;
    const p2 = byte & 0x0f;

    const c1 = TITLE_PALETTE_RGBA[p1] || TITLE_PALETTE_RGBA[0];
    const off1 = (i * 2) * 4;
    data[off1] = c1[0];
    data[off1 + 1] = c1[1];
    data[off1 + 2] = c1[2];
    data[off1 + 3] = c1[3];

    if (i * 2 + 1 < TITLE_LOGO_WIDTH * TITLE_LOGO_HEIGHT) {
      const c2 = TITLE_PALETTE_RGBA[p2] || TITLE_PALETTE_RGBA[0];
      const off2 = (i * 2 + 1) * 4;
      data[off2] = c2[0];
      data[off2 + 1] = c2[1];
      data[off2 + 2] = c2[2];
      data[off2 + 3] = c2[3];
    }
  }

  ctx.putImageData(imgData, 0, 0);
}
